import {
  Activity,
  ArrowUpRight,
  Clock3,
  ExternalLink,
  MapPin,
  MonitorSmartphone,
  MousePointerClick,
  Users,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatTile } from '@/components/marketing/MetricTile';
import { RankedList } from '@/components/marketing/RankedList';
import { TrendChart } from '@/components/marketing/TrendChart';
import {
  ChannelStatusNotice,
  resolveChannelStatus,
} from '@/components/marketing/ChannelStatusNotice';
import { comparisonLabel } from '@/components/marketing/RangeSelector';
import { CHART_SERIES } from '@/lib/brand';
import { useWebAnalytics } from '@/features/intelligence/hooks';
import { useWebsiteTotals } from '@/features/marketing/hooks';
import {
  formatCompactNumber,
  formatDuration,
  formatRatioPercent,
  MARKETING_RANGE_DAYS,
  pctChange,
  type MarketingRange,
} from '@/features/marketing/range';

const SITE = 'virrolife.com';

interface WebsiteTabProps {
  range: MarketingRange;
}

/**
 * Website performance from Google Analytics 4.
 *
 * Pairs two hooks by design: `useWebsiteTotals` supplies the headline numbers
 * with a period-over-period comparison, while `useWebAnalytics` (shared with
 * the standalone Web Analytics page) supplies the traffic-source, page,
 * device, and geography breakdowns. Splitting it this way avoids duplicating
 * GA4's dimension rollups here — see the note above `useWebsiteTotals`.
 */
export function WebsiteTab({ range }: WebsiteTabProps) {
  const totalsQuery = useWebsiteTotals(range);
  const breakdownQuery = useWebAnalytics(range);

  const days = MARKETING_RANGE_DAYS[range];
  const totals = totalsQuery.data?.totals;
  const previous = totalsQuery.data?.previous;
  const deltaLabel = comparisonLabel(range);
  const loading = totalsQuery.isLoading;

  const status = resolveChannelStatus({
    isLoading: totalsQuery.isLoading,
    error: totalsQuery.error,
    connected: totalsQuery.data?.connected,
  });

  const breakdown = breakdownQuery.data;

  return (
    <div className="space-y-6">
      <ChannelStatusNotice
        status={status}
        channel="Google Analytics 4"
        error={totalsQuery.error instanceof Error ? totalsQuery.error.message : null}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium tracking-headline text-foreground">
            {SITE}
          </h2>
        </div>
        <Button variant="outline" size="sm" asChild>
          <a href="https://analytics.google.com" target="_blank" rel="noreferrer">
            Open GA4 <ExternalLink className="h-4 w-4" />
          </a>
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Users"
          icon={Users}
          loading={loading}
          value={formatCompactNumber(totals?.users ?? 0)}
          delta={pctChange(totals?.users ?? 0, previous?.users ?? 0)}
          deltaLabel={deltaLabel}
          hint={
            totals
              ? `${formatCompactNumber(totals.newUsers)} new (${formatRatioPercent(totals.newUserShare)})`
              : undefined
          }
        />
        <StatTile
          label="Sessions"
          icon={Activity}
          loading={loading}
          value={formatCompactNumber(totals?.sessions ?? 0)}
          delta={pctChange(totals?.sessions ?? 0, previous?.sessions ?? 0)}
          deltaLabel={deltaLabel}
          hint={
            totals
              ? `${Math.round(totals.sessions / days).toLocaleString()} per day average`
              : undefined
          }
        />
        <StatTile
          label="Engagement rate"
          icon={MousePointerClick}
          loading={loading}
          value={formatRatioPercent(totals?.engagementRate ?? 0)}
          delta={pctChange(totals?.engagementRate ?? 0, previous?.engagementRate ?? 0)}
          deltaLabel={deltaLabel}
          hint={
            totals
              ? `${formatDuration(totals.avgEngagementSeconds)} avg per session`
              : undefined
          }
        />
        <StatTile
          label="Conversions"
          icon={ArrowUpRight}
          loading={loading}
          value={formatCompactNumber(totals?.conversions ?? 0)}
          delta={pctChange(totals?.conversions ?? 0, previous?.conversions ?? 0)}
          deltaLabel={deltaLabel}
          hint={
            totals
              ? `${formatRatioPercent(totals.conversionRate, 2)} of sessions convert`
              : undefined
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Traffic over time</CardTitle>
        </CardHeader>
        <CardContent>
          <TrendChart
            data={totalsQuery.data?.daily ?? []}
            height={300}
            series={[
              { key: 'users', label: 'Users', color: CHART_SERIES[0], kind: 'area' },
              { key: 'sessions', label: 'Sessions', color: CHART_SERIES[1], kind: 'line' },
              {
                key: 'newUsers',
                label: 'New users',
                color: CHART_SERIES[3],
                kind: 'line',
                dashed: true,
              },
            ]}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Traffic sources</CardTitle>
          </CardHeader>
          <CardContent>
            <RankedList
              rows={(breakdown?.sources ?? []).slice(0, 7).map((row) => ({
                key: row.key,
                label: row.label,
                value: row.users,
                share: row.share,
                hint: `${formatCompactNumber(row.sessions)} sessions`,
              }))}
              formatValue={formatCompactNumber}
              emptyLabel="No traffic-source data for this period."
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top pages</CardTitle>
          </CardHeader>
          <CardContent>
            <RankedList
              rows={(breakdown?.pages ?? []).slice(0, 7).map((row) => ({
                key: row.key,
                label: row.key,
                value: row.views ?? row.sessions,
                share: row.share,
                hint: row.hint,
              }))}
              formatValue={formatCompactNumber}
              emptyLabel="No page data for this period."
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MonitorSmartphone className="h-4 w-4 text-muted-foreground" />
              Devices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RankedList
              compact
              rows={(breakdown?.devices ?? []).slice(0, 5).map((row) => ({
                key: row.key,
                label: row.label,
                value: row.sessions,
                share: row.share,
              }))}
              formatValue={formatCompactNumber}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              Top countries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RankedList
              compact
              rows={(breakdown?.geo ?? []).slice(0, 6).map((row) => ({
                key: row.key,
                label: row.label,
                value: row.users,
                share: row.share,
              }))}
              formatValue={formatCompactNumber}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-muted-foreground" />
              Key events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RankedList
              compact
              rows={pickKeyEvents(breakdown?.events ?? []).map((row) => ({
                key: row.key,
                label: row.label,
                value: row.users,
                share: row.share,
              }))}
              formatValue={formatCompactNumber}
              emptyLabel="No key events marked in GA4 yet."
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * Prefers GA4's explicitly-marked key events, falling back to the overall top
 * events when the property hasn't flagged any — otherwise this panel reads as
 * "no conversions" on a property that simply hasn't configured them.
 */
function pickKeyEvents<T extends { hint?: string }>(events: T[]): T[] {
  const keyOnly = events.filter((event) => event.hint === 'Key event');
  return (keyOnly.length > 0 ? keyOnly : events).slice(0, 6);
}
